import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { REGEX } from 'utils/regex';

const ORDER_NAV_STATE_STORAGE_KEY = 'lineAssignments:orderNavigationState';
const REQUIRED_DOCUMENTS_FOR_VALIDATION = [
  { id: 1, nameFromResponse: 'articles_of_incorporation' },
  { id: 2, nameFromResponse: 'representative_id' },
  { id: 3, nameFromResponse: 'tax_id_certificate' },
  { id: 4, nameFromResponse: 'proof_of_address' },
];

const DOCUMENT_RESPONSE_KEY_BY_TYPE_ID = {
  1: 'articles_of_incorporation',
  2: 'representative_id',
  3: 'tax_id_certificate',
  4: 'proof_of_address',
  5: 'legal_representative_appointment',
  6: 'other',
};

const getPersistedOrderNavigationState = () => {
  try {
    const persistedState = sessionStorage.getItem(ORDER_NAV_STATE_STORAGE_KEY);
    return persistedState ? JSON.parse(persistedState) : {};
  } catch {
    return {};
  }
};

export const useAssignmentOrder = ({
  orderAssignmentDetail,
  getOrderAssignmentDetail,
  cleanOrderAssignmentDetail,
  updateOrderLineAssignment,
  updateOrderLineAssignmentAction,
  cleanUpdateOrderLineAssignment,
  orderAssignmentCreateUrlDocumentAction,
  cleanOrderAssignmentCreateUrlDocument,
  orderAssignmentUploadDocumentAction,
  cleanOrderAssignmentUploadDocument,
  orderAssignmentConfirmUploadDocumentAction,
  cleanOrderAssignmentConfirmUploadDocument,
  removeAssignmentDocument,
  removeAssignmentDocumentAction,
  cleanRemoveAssignmentDocument,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogTitleMessage, setDialogTitleMessage] = useState('');
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [selectedFilesByDocument, setSelectedFilesByDocument] = useState({});
  const [activeTypeDocumentId, setActiveTypeDocumentId] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [activeDocumentAction, setActiveDocumentAction] = useState(null);
  const [pendingFileName, setPendingFileName] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const navigationState =
    location.state && Object.keys(location.state).length > 0
      ? location.state
      : getPersistedOrderNavigationState();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: {
      rfc: '',
      legalName: '',
      tradeName: '',
      contactPhone: '',
      contactEmail: '',
      legalZipCode: '',
      legalAddress: '',
      legalRepPhone: '',
      legalRepEmail: '',
    },
  });

  const taxDataValidations = {
    rfc: {
      required: 'El RFC es requerido',
      pattern: {
        value: REGEX.rfc,
        message: 'El RFC debe tener 12 o 13 caracteres alfanumericos en mayusculas',
      },
    },
    legalName: {
      required: 'La razon social es requerida',
      minLength: {
        value: 3,
        message: 'La razon social debe tener al menos 3 caracteres',
      },
    },
    tradeName: {
      required: 'El nombre comercial es requerido',
      minLength: {
        value: 3,
        message: 'El nombre comercial debe tener al menos 3 caracteres',
      },
    },
    contactPhone: {
      required: 'El telefono es requerido',
      pattern: {
        value: REGEX.phone,
        message: 'El telefono debe tener 10 digitos',
      },
    },
    contactEmail: {
      required: 'El correo electronico es requerido',
      pattern: {
        value: REGEX.email,
        message: 'Ingresa un correo electronico valido',
      },
    },
    legalZipCode: {
      required: 'El código postal es requerido',
      pattern: {
        value: REGEX.zipCode,
        message: 'El código postal debe tener 5 dígitos',
      },
    },
    legalAddress: {
      required: 'La dirección es requerida',
      minLength: {
        value: 5,
        message: 'La dirección debe tener al menos 5 caracteres',
      },
    },
    legalRepPhone: {
      required: 'El telefono del representante legal es requerido',
      pattern: {
        value: REGEX.phone,
        message: 'El telefono debe tener 10 digitos',
      },
    },
    legalRepEmail: {
      required: 'El correo del representante legal es requerido',
      pattern: {
        value: REGEX.email,
        message: 'Ingresa un correo electronico valido',
      },
    },
  };

  const areRequiredDocumentsLoaded = useMemo(() => {
    return REQUIRED_DOCUMENTS_FOR_VALIDATION.every((document) => {
      const hasSelectedDocument = Boolean(selectedFilesByDocument?.[document.id]);
      const responseDocument =
        document.nameFromResponse && uploadedDocuments
          ? uploadedDocuments?.[document.nameFromResponse]
          : null;
      const hasUploadedDocumentFromApi = Boolean(
        responseDocument?.url || responseDocument?.uploaded,
      );
      console.log('validating document', document.id, {
        hasSelectedDocument,
        hasUploadedDocumentFromApi,
        responseDocument,
      });
      return hasSelectedDocument || hasUploadedDocumentFromApi;
    });
  }, [selectedFilesByDocument, uploadedDocuments]);

  const setDataForm = useCallback(
    (data) => {
      if (!data || typeof data !== 'object') {
        return;
      }

      //company_profile
      setValue('legalName', data?.company_profile?.legal_name || '');
      setValue('tradeName', data?.company_profile?.trade_name || '');
      setValue('contactPhone', data?.company_profile?.contact?.phone || '');
      setValue('contactEmail', data?.company_profile?.contact?.email || '');
      setValue('legalZipCode', data?.company_profile?.legal_zipcode || '');
      setValue('legalAddress', data?.company_profile?.legal_address || '');

      //legal_representative
      setValue('legalRepPhone', data?.legal_representative?.phone || '');
      setValue('legalRepEmail', data?.legal_representative?.email || '');

      //documents
      setUploadedDocuments(data?.documents || {});
    },
    [setValue],
  );

  useEffect(() => {
    const searchFormData = navigationState?.searchFormData;
    const isProcessOrder = navigationState?.isProcessOrder;
    const orderId = navigationState?.order_id;

    setValue('rfc', searchFormData?.legal_id || navigationState?.legal_id || '');

    if (isProcessOrder && orderId) {
      setIsLoading(true);
      const params = {
        legal_id: searchFormData?.legal_id || navigationState?.legal_id,
        order_id: orderId,
      };
      getOrderAssignmentDetail(params).finally(() => setIsLoading(false));
    }

    return () => {
      cleanOrderAssignmentDetail();
    };
  }, [getOrderAssignmentDetail, cleanOrderAssignmentDetail, navigationState, setValue]);

  useEffect(() => {
    if (!location.state || Object.keys(location.state).length === 0) {
      return;
    }

    sessionStorage.setItem(ORDER_NAV_STATE_STORAGE_KEY, JSON.stringify(location.state));
  }, [location.state]);

  useEffect(() => {
    if (orderAssignmentDetail?.status === 200) {
      setIsLoading(false);
      setDataForm(orderAssignmentDetail?.payload);
    } else if (orderAssignmentDetail?.payload?.response?.status > 200) {
      setDialogTitleMessage(orderAssignmentDetail?.payload?.response?.data?.title);
      setDialogMessage(
        orderAssignmentDetail?.payload?.response?.data?.detail ?? 'Ocurrió un error.',
      );
      setIsDialogOpen(true);
      setIsLoading(false);
    }
  }, [orderAssignmentDetail, setDataForm]);

  useEffect(() => {
    if (updateOrderLineAssignment?.status === 200) {
      setIsLoading(false);
      navigate('/line-assignments/lines', {
        state: {
          order_id: navigationState?.order_id,
          legal_id: navigationState?.searchFormData?.legal_id || navigationState?.legal_id,
          legalName: getValues('legalName'),
          searchFormData: navigationState?.searchFormData,
        },
      });
      cleanUpdateOrderLineAssignment();
    } else if (updateOrderLineAssignment?.payload?.response?.status > 200) {
      setDialogTitleMessage(updateOrderLineAssignment?.payload?.response?.data?.title);
      setDialogMessage(
        updateOrderLineAssignment?.payload?.response?.data?.detail ?? 'Ocurrió un error.',
      );
      setIsDialogOpen(true);
      setIsLoading(false);
      cleanUpdateOrderLineAssignment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateOrderLineAssignment]);

  useEffect(() => {
    if (removeAssignmentDocument?.status === 200) {
      setSelectedFilesByDocument((prev) => {
        if (!activeTypeDocumentId) {
          return prev;
        }

        const next = { ...prev };
        delete next[activeTypeDocumentId];
        return next;
      });

      setUploadedDocuments((prev) => {
        if (!activeTypeDocumentId || !prev || typeof prev !== 'object') {
          return prev;
        }

        const responseKey = DOCUMENT_RESPONSE_KEY_BY_TYPE_ID?.[activeTypeDocumentId];

        if (!responseKey || !prev?.[responseKey]) {
          return prev;
        }

        return {
          ...prev,
          [responseKey]: {
            ...prev[responseKey],
            uploaded: false,
            url: '',
          },
        };
      });

      setActiveTypeDocumentId(null);
      setActiveDocumentAction(null);
      setPendingFileName('');
      setIsLoadingDocument(false);
    } else if (removeAssignmentDocument?.payload?.response?.status > 200) {
      setDialogTitleMessage(removeAssignmentDocument?.payload?.response?.data?.title);
      setDialogMessage(
        removeAssignmentDocument?.payload?.response?.data?.detail ?? 'Ocurrió un error.',
      );
      setIsDialogOpen(true);
      setActiveTypeDocumentId(null);
      setActiveDocumentAction(null);
      setPendingFileName('');
      setIsLoadingDocument(false);
    }
  }, [removeAssignmentDocument, activeTypeDocumentId]);

  const handleClose = () => {
    setIsDialogOpen(false);
    setDialogMessage('');
    setDialogTitleMessage('');
    cleanUpdateOrderLineAssignment();
    cleanOrderAssignmentDetail();
    cleanOrderAssignmentCreateUrlDocument();
    cleanOrderAssignmentUploadDocument();
    cleanOrderAssignmentConfirmUploadDocument();
    cleanRemoveAssignmentDocument();
  };

  const onSubmitTaxData = (data) => {
    setIsLoading(true);
    const body = {
      legal_name: data.legalName,
      trade_name: data.tradeName,
      company_contact_phone: data.contactPhone,
      company_contact_email: data.contactEmail,
      representative_phone: data.legalRepPhone,
      representative_email: data.legalRepEmail,
      legal_zipcode: data.legalZipCode,
      legal_address: data.legalAddress,
    };
    updateOrderLineAssignmentAction({
      legal_id: data.rfc,
      order_id: navigationState?.order_id,
      body,
    });
  };

  const createUrl = (data) => {
    const { document_type_id, file_name, content_type, file } = data || {};

    if (!file) {
      return;
    }

    setActiveTypeDocumentId(document_type_id);
    setActiveDocumentAction('upload');
    setPendingFileName(file.name || file_name || '');
    setIsLoadingDocument(true);

    const body = {
      document_type_id,
      file_name,
      content_type,
    };

    orderAssignmentCreateUrlDocumentAction({
      legal_id: navigationState?.searchFormData?.legal_id || navigationState?.legal_id,
      order_id: navigationState?.order_id,
      body,
    })
      .then((createUrlResponse) => {
        const createUrlPayload = createUrlResponse?.response || createUrlResponse || {};
        const { upload_url, document_id } = createUrlPayload;

        if (!upload_url || !document_id) {
          throw new Error('No se recibió upload_url o document_id para procesar el documento.');
        }

        setDocumentId(document_id);

        return orderAssignmentUploadDocumentAction({
          upload_url,
          file,
          content_type,
        }).then(() => document_id);
      })
      .then((document_id) => {
        setSelectedFilesByDocument((prev) => ({
          ...prev,
          [document_type_id]: {
            documentId: document_id,
            file,
          },
        }));
        return orderAssignmentConfirmUploadDocumentAction({
          legal_id: navigationState?.searchFormData?.legal_id || navigationState?.legal_id,
          order_id: navigationState?.order_id,
          document_id,
        });
      })
      .catch((error) => {
        setDialogTitleMessage(error?.response?.data?.title ?? 'Error al subir documento');
        setDialogMessage(error?.response?.data?.detail ?? error?.message ?? 'Ocurrió un error.');
        setIsDialogOpen(true);
        cleanOrderAssignmentCreateUrlDocument();
        cleanOrderAssignmentUploadDocument();
        cleanOrderAssignmentConfirmUploadDocument();
      })
      .finally(() => {
        setActiveTypeDocumentId(null);
        setActiveDocumentAction(null);
        setPendingFileName('');
        setIsLoadingDocument(false);
      });
  };

  const removeDocument = async (document_type_id, uploadedDocumentIdFromApi = null) => {
    const selectedDocument = selectedFilesByDocument?.[document_type_id];
    const uploadedDocumentId = selectedDocument?.documentId || uploadedDocumentIdFromApi;

    if (!uploadedDocumentId) {
      return;
    }

    setActiveTypeDocumentId(document_type_id);
    setActiveDocumentAction('remove');
    setPendingFileName('');
    setIsLoadingDocument(true);
    removeAssignmentDocumentAction({
      legal_id: navigationState?.searchFormData?.legal_id || navigationState?.legal_id,
      order_id: navigationState?.order_id,
      document_id: uploadedDocumentId,
    });
  };

  const navigateToSearchPage = () => {
    navigate('/line-assignments/search-orders', {
      state: {
        searchFormData: navigationState?.searchFormData,
      },
    });
    cleanOrderAssignmentDetail();
    cleanUpdateOrderLineAssignment();
  };

  return {
    navigateToSearchPage,
    register,
    errors,
    taxDataValidations,
    handleSubmitTaxData: handleSubmit(onSubmitTaxData),
    isLoading,
    isDialogOpen,
    dialogMessage,
    dialogTitleMessage,
    handleClose,
    createUrl,
    removeDocument,
    isLoadingDocument,
    selectedFilesByDocument,
    activeTypeDocumentId,
    activeDocumentAction,
    pendingFileName,
    documentId,
    setValue,
    uploadedDocuments,
    areRequiredDocumentsLoaded,
    isTermsAccepted,
    setIsTermsAccepted,
  };
};
